import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Zap, LogIn, LogOut, LayoutDashboard, RefreshCcw, Radio, Calendar } from "lucide-react";
import LiveScoresTicker from "@/components/LiveScoresTicker";
import AddToSlipButton from "@/components/AddToSlipButton";

const WINDOWS = [
    { id: "today", label: "Leo" },
    { id: "live", label: "Moja kwa Moja" },
    { id: "recent", label: "Za Hivi Karibuni" },
    { id: "upcoming", label: "Zijazo" },
];

const ScoreboardPage = () => {
    const { user, logout, isContentManager } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const initialWindow = (() => {
        const w = new URLSearchParams(location.search).get("window");
        return WINDOWS.some((x) => x.id === w) ? w : "today";
    })();
    const [matches, setMatches] = useState([]);
    const [leagues, setLeagues] = useState([]);
    const [selectedWindow, setSelectedWindow] = useState(initialWindow);
    const [selectedLeague, setSelectedLeague] = useState("all");
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchScores = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/live-scores?window=${selectedWindow}`);
            setMatches(data?.matches || []);
            setLeagues(data?.leagues || []);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Error fetching scoreboard:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchScores();
        const timer = setInterval(fetchScores, 60000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWindow]);

    const filtered =
        selectedLeague === "all"
            ? matches
            : matches.filter((m) => m.competition_id === selectedLeague);

    // Group by competition
    const grouped = filtered.reduce((acc, m) => {
        const key = m.competition_id;
        if (!acc[key]) {
            acc[key] = {
                competition_id: key,
                competition_name: m.competition_name,
                competition_name_sw: m.competition_name_sw,
                flag: m.flag,
                matches: [],
            };
        }
        acc[key].matches.push(m);
        return acc;
    }, {});

    const hasLive = matches.some((m) => m.state === "in");

    return (
        <div className="min-h-screen bg-obsidian" data-testid="scoreboard-page">
            {/* Top Nav */}
            <nav className="bg-electric">
                <div className="container-main">
                    <div className="flex items-center justify-between h-12">
                        <Link to="/" className="flex items-center gap-2">
                            <Zap className="h-6 w-6 text-white" />
                            <span className="dimbani-wordmark text-2xl text-white">
                                DIMBANI
                            </span>
                        </Link>
                        <div className="hidden md:flex items-center gap-3">
                            {user ? (
                                <>
                                    {isContentManager && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => navigate("/admin")}
                                            className="text-white hover:bg-white/10"
                                        >
                                            <LayoutDashboard className="h-4 w-4 mr-1" /> Admin
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={logout}
                                        className="text-white hover:bg-white/10"
                                    >
                                        <LogOut className="h-4 w-4" />
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate("/login")}
                                    className="text-white hover:bg-white/10 font-bold"
                                >
                                    <LogIn className="h-4 w-4 mr-1" /> Ingia
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Ticker */}
            <LiveScoresTicker />

            {/* Page Header */}
            <div className="bg-paper border-b border-border">
                <div className="container-main py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="font-heading font-black text-3xl md:text-4xl text-white uppercase tracking-tight">
                                Matokeo ya Mechi
                            </h1>
                            <p className="text-white/60 text-sm mt-1">
                                Matokeo na ratiba ya mechi za kandanda kutoka mashindano makuu duniani
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {hasLive && (
                                <Badge className="bg-red-600 text-white animate-pulse">
                                    <Radio className="h-3 w-3 mr-1" /> LIVE
                                </Badge>
                            )}
                            {lastUpdated && (
                                <span className="text-white/40 text-xs">
                                    Imesasishwa: {lastUpdated.toLocaleTimeString("sw-TZ")}
                                </span>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={fetchScores}
                                disabled={loading}
                                className="border-electric/30 text-electric hover:bg-electric/10"
                                data-testid="refresh-scoreboard-btn"
                            >
                                <RefreshCcw
                                    className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`}
                                />
                                Sasisha
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Window Filters */}
            <div className="bg-subtle border-b border-border">
                <div className="container-main">
                    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide h-12">
                        {WINDOWS.map((w) => (
                            <button
                                key={w.id}
                                onClick={() => setSelectedWindow(w.id)}
                                data-testid={`window-${w.id}`}
                                className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                    selectedWindow === w.id
                                        ? "border-electric text-electric"
                                        : "border-transparent text-white/60 hover:text-white"
                                }`}
                            >
                                {w.label}
                            </button>
                        ))}
                        <Separator orientation="vertical" className="h-6 mx-2 bg-border" />
                        <button
                            onClick={() => setSelectedLeague("all")}
                            className={`px-3 py-1 text-xs font-medium rounded whitespace-nowrap ${
                                selectedLeague === "all"
                                    ? "bg-electric/20 text-electric"
                                    : "text-white/50 hover:text-white"
                            }`}
                        >
                            Ligi Zote
                        </button>
                        {leagues.map((lg) => (
                            <button
                                key={lg.id}
                                onClick={() => setSelectedLeague(lg.id)}
                                className={`px-3 py-1 text-xs font-medium rounded whitespace-nowrap flex items-center gap-1 ${
                                    selectedLeague === lg.id
                                        ? "bg-electric/20 text-electric"
                                        : "text-white/50 hover:text-white"
                                }`}
                                data-testid={`league-filter-${lg.id}`}
                            >
                                <span>{lg.flag}</span>
                                <span>{lg.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content */}
            <main className="container-main py-6">
                {loading && matches.length === 0 ? (
                    <div className="text-center py-16 text-white/40">Inapakia matokeo...</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16">
                        <Calendar className="h-12 w-12 text-white/20 mx-auto mb-3" />
                        <p className="text-white/60">Hakuna mechi kwenye kipindi hiki.</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {Object.values(grouped).map((group) => (
                            <div key={group.competition_id} data-testid={`league-group-${group.competition_id}`}>
                                <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-electric/40">
                                    <span className="text-xl">{group.flag}</span>
                                    <h2 className="font-heading font-bold text-lg text-white uppercase">
                                        {group.competition_name_sw}
                                    </h2>
                                    <span className="text-white/40 text-xs ml-2">
                                        {group.matches.length} mechi
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                    {group.matches.map((m, idx) => (
                                        <MatchCard key={m.id} match={m} idx={idx} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

const SWAHILI_DAYS = ["Jumapili", "Jumatatu", "Jumanne", "Jumatano", "Alhamisi", "Ijumaa", "Jumamosi"];
const SWAHILI_MONTHS = ["Jan", "Feb", "Mac", "Apr", "Mei", "Jun", "Jul", "Ago", "Sep", "Okt", "Nov", "Des"];

const formatSwahiliKickoff = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        const day = SWAHILI_DAYS[d.getDay()];
        const date = d.getDate();
        const month = SWAHILI_MONTHS[d.getMonth()];
        const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
        return { date: `${day} · ${date} ${month}`, time };
    } catch (_) {
        return null;
    }
};

const MatchCard = ({ match, idx }) => {
    const isLive = match.state === "in";
    const isFinished = match.state === "post";
    const isUpcoming = match.state === "pre";
    const homeScore = match.home_score;
    const awayScore = match.away_score;
    const homeWinner = isFinished && Number(homeScore) > Number(awayScore);
    const awayWinner = isFinished && Number(awayScore) > Number(homeScore);
    const kickoff = isUpcoming ? formatSwahiliKickoff(match.date) : null;
    const finishedDate = isFinished ? formatSwahiliKickoff(match.date) : null;

    return (
        <Card
            className={`bg-paper border-border overflow-hidden hover:border-electric/40 transition-colors ${isUpcoming ? "cursor-pointer" : ""}`}
            data-testid={`scoreboard-match-${idx}`}
        >
            {/* Status Bar — ESPN-style */}
            <div className="flex items-center justify-between px-4 py-2 bg-subtle border-b border-border gap-2">
                <span className="text-white/50 text-[11px] uppercase tracking-wider truncate">
                    {isUpcoming && kickoff ? (
                        <>
                            <span className="text-white/80 font-bold">{kickoff.date}</span>
                            <span className="text-white/30 mx-1">·</span>
                            <span className="text-electric font-mono font-bold">{kickoff.time}</span>
                            {match.venue && (
                                <span className="text-white/30 ml-2 normal-case truncate">· {match.venue}</span>
                            )}
                        </>
                    ) : isFinished && finishedDate ? (
                        <>
                            <span className="text-white/60">{finishedDate.date}</span>
                            {match.venue && (
                                <span className="text-white/30 ml-2 normal-case truncate">· {match.venue}</span>
                            )}
                        </>
                    ) : (
                        match.venue || "Moja kwa Moja"
                    )}
                </span>
                <span
                    className={`shrink-0 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${
                        isLive
                            ? "bg-red-500 text-white animate-pulse"
                            : isFinished
                            ? "bg-white/10 text-white/70"
                            : "bg-electric/15 text-electric"
                    }`}
                >
                    {isUpcoming ? "Bado" : match.minute_label}
                </span>
            </div>

            {/* Teams */}
            <div className="p-4 space-y-3">
                <TeamRow
                    team={match.home_name}
                    abbr={match.home_abbr}
                    logo={match.home_logo}
                    score={homeScore}
                    state={match.state}
                    isWinner={homeWinner}
                />
                <TeamRow
                    team={match.away_name}
                    abbr={match.away_abbr}
                    logo={match.away_logo}
                    score={awayScore}
                    state={match.state}
                    isWinner={awayWinner}
                />
            </div>

            {isUpcoming && match.id && (
                <div className="border-t border-border flex items-stretch divide-x divide-border">
                    <Link
                        to={`/ratiba/${match.id}?league=${match.competition_id}&sport=${match.sport || "soccer"}`}
                        className="flex-1 text-center bg-subtle text-electric hover:bg-electric hover:text-white text-xs font-bold py-2.5 transition-colors uppercase tracking-wider"
                        data-testid={`preview-link-${idx}`}
                    >
                        Uchambuzi →
                    </Link>
                    <div className="bg-subtle px-3 py-2 flex items-center">
                        <AddToSlipButton
                            idx={idx}
                            fixture={{
                                id: match.id,
                                event_id: match.id,
                                home_team: match.home_name,
                                away_team: match.away_name,
                                home_logo: match.home_logo,
                                away_logo: match.away_logo,
                                home_abbr: match.home_abbr,
                                away_abbr: match.away_abbr,
                                competition_id: match.competition_id,
                                competition_name: match.competition_name,
                                competition_name_sw: match.competition_name_sw,
                                flag: match.flag,
                                kickoff_iso: match.date,
                            }}
                        />
                    </div>
                </div>
            )}
        </Card>
    );
};

const TeamRow = ({ team, abbr, logo, score, state, isWinner }) => (
    <div className={`flex items-center justify-between gap-3 ${state === "post" && !isWinner ? "opacity-50" : ""}`}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
            {logo ? (
                <img src={logo} alt={abbr} className="h-9 w-9 object-contain shrink-0" />
            ) : (
                <div className="h-9 w-9 rounded-full bg-white/10 shrink-0 flex items-center justify-center text-white/40 text-[10px] font-bold">
                    {abbr || "?"}
                </div>
            )}
            <div className="min-w-0">
                <div className="text-white text-sm font-medium truncate flex items-center gap-1.5">
                    {team}
                    {isWinner && <span className="text-electric text-[10px]">▸</span>}
                </div>
                <div className="text-white/40 text-[10px] uppercase tracking-wider font-mono">{abbr}</div>
            </div>
        </div>
        <span
            className={`font-mono font-black w-8 text-right text-2xl shrink-0 ${
                state === "in"
                    ? "text-red-400"
                    : state === "post"
                    ? isWinner ? "text-white" : "text-white/40"
                    : "text-white/20"
            }`}
        >
            {score ?? "—"}
        </span>
    </div>
);

export default ScoreboardPage;
